import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import roleManager from '../utils/roleManager';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import { filterNav, flattenNav, toSearchItems } from '../utils/mobileNav';
import './CommandPalette.css';

// One launcher for two jobs: on desktop it is the command palette (Ctrl/Cmd+K
// or "/" from anywhere), on a phone it is the full-screen search. Same
// content either way: pages you can jump to, plus customers, tickets and
// invoices from the app-wide search. Fully keyboard driven: arrows move,
// Enter opens, Esc closes.
export default function CommandPalette({ open, onClose, variant = 'palette' }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const { searchResults, customerResults, isSearching, hasQuery } = useGlobalSearch(open ? query : '');

  const nav = useMemo(() => flattenNav(
    [{ path: '/home', label: 'Home', icon: 'fas fa-house' }, ...roleManager.getVisibleNavigationItems()],
    [{ path: '/settings', label: 'Settings', icon: 'fas fa-cog', section: 'Account' }]
  ), [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Everything shown, in display order: pages first, then search hits.
  const entries = useMemo(() => {
    const pages = filterNav(nav, query).slice(0, query.trim() ? 8 : 12).map((e) => ({
      key: `page-${e.path}`, group: 'Go to', icon: e.icon, title: e.label, subtitle: e.section || '', path: e.path
    }));
    return [...pages, ...toSearchItems({ customerResults, searchResults })];
  }, [nav, query, customerResults, searchResults]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setIndex(0);
    // after the overlay is in the DOM
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => { setIndex(0); }, [query]);

  // keep the highlighted row in view
  useEffect(() => {
    const el = listRef.current && listRef.current.querySelector('[aria-selected="true"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [index, entries]);

  if (!open) return null;

  const go = (entry) => {
    onClose();
    navigate(entry.path);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndex((i) => Math.min(i + 1, entries.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && entries[index]) {
      e.preventDefault();
      go(entries[index]);
    }
  };

  let lastGroup = null;
  const placeholder = variant === 'sheet' ? 'Search or jump to…' : 'Jump to a page, or search customers, tickets, invoices…';

  return (
    <div className={`cmdk-root cmdk-${variant}`} onKeyDown={onKeyDown}>
      <div className="cmdk-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="cmdk-panel" role="dialog" aria-modal="true" aria-label="Search and navigate">
        <div className="cmdk-input-row">
          <i className="fas fa-magnifying-glass" aria-hidden="true"></i>
          <input
            ref={inputRef}
            type="search"
            className="cmdk-input"
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-list"
            aria-activedescendant={entries[index] ? `cmdk-opt-${index}` : undefined}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="go"
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {variant === 'sheet'
            ? <button type="button" className="cmdk-cancel" onClick={onClose}>Cancel</button>
            : <kbd className="cmdk-esc">Esc</kbd>}
        </div>

        <div className="cmdk-list" id="cmdk-list" role="listbox" ref={listRef}>
          {entries.map((entry, i) => {
            const header = entry.group !== lastGroup ? entry.group : null;
            lastGroup = entry.group;
            return (
              <div key={entry.key}>
                {header && <div className="cmdk-group" role="presentation">{header}</div>}
                <div
                  id={`cmdk-opt-${i}`}
                  role="option"
                  aria-selected={i === index}
                  className={`cmdk-item ${i === index ? 'is-active' : ''}`}
                  onMouseMove={() => i !== index && setIndex(i)}
                  onClick={() => go(entry)}
                >
                  <span className="cmdk-icon"><i className={entry.icon} aria-hidden="true"></i></span>
                  <span className="cmdk-text">
                    <span className="cmdk-title">{entry.title}</span>
                    {entry.subtitle && <span className="cmdk-subtitle">{entry.subtitle}</span>}
                  </span>
                  {i === index && variant === 'palette' && <kbd className="cmdk-enter">Enter</kbd>}
                </div>
              </div>
            );
          })}
          {isSearching && <div className="cmdk-status" aria-live="polite">Searching&hellip;</div>}
          {!isSearching && hasQuery && entries.length === 0 && (
            <div className="cmdk-status">Nothing matches &ldquo;{query.trim()}&rdquo;.</div>
          )}
        </div>
      </div>
    </div>
  );
}
